import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { appConfirmMessage } from '../../../utils/appConfirm';
import {
  FaSearch,
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaShoppingCart,
  FaClock,
  FaDollarSign,
  FaExclamationTriangle,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { formatAUD } from '../../../utils/formatCurrency';
import { useAdminListOverlay } from '../../../utils/useAdminPageSearch';
import PurchaseOrderForm from './PurchaseOrderForm';
import PurchaseOrderDetailModal from './PurchaseOrderDetailModal';
import '../styles/InventoryProcurement.css';

const FIND = `
  query FindAllPOs {
    findAllPurchaseOrders {
      id poNumber orderDate expectedDelivery requestedBy status
      subtotal gst total lineCount
      supplier { id name code }
      lines { id description quantity quantityReceived unitCost lineTotal itemId }
    }
  }
`;

const REMOVE = `
  mutation RemovePO($id: String!) {
    removePurchaseOrder(id: $id)
  }
`;

const TABS = [
  { id: 'all', label: 'All' },
  { id: 'Draft', label: 'Draft' },
  { id: 'PendingApproval', label: 'Pending Approval' },
  { id: 'Ordered', label: 'Ordered' },
  { id: 'PartiallyReceived', label: 'Partially Received' },
  { id: 'Received', label: 'Received' },
];
const TAB_IDS = TABS.map((t) => t.id);

const PAGE_SIZE = 8;

function statusLabel(status) {
  const s = String(status || '');
  if (s === 'PendingApproval') return 'Pending Approval';
  if (s === 'PartiallyReceived') return 'Partially Received';
  return s || '—';
}

function statusClass(status) {
  const s = String(status || '').toLowerCase();
  if (s.includes('draft')) return 'iproc-pill--draft';
  if (s.includes('pending')) return 'iproc-pill--pending';
  if (s.includes('partial')) return 'iproc-pill--partial';
  if (s.includes('received')) return 'iproc-pill--received';
  if (s.includes('cancel')) return 'iproc-pill--cancelled';
  return 'iproc-pill--ordered';
}

function formatDate(raw) {
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function isOverdue(row) {
  if (!row.expectedDelivery) return false;
  const s = String(row.status || '').toLowerCase();
  if (s.includes('received') || s.includes('cancel')) return false;
  const d = new Date(row.expectedDelivery);
  return d < new Date();
}

export default function PurchaseOrdersDashboard() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const overlay = useAdminListOverlay(rows, { allowedTabs: TAB_IDS });
  const tab = overlay.tab;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await graphqlRequest(FIND);
      setRows(Array.isArray(data?.findAllPurchaseOrders) ? data.findAllPurchaseOrders : []);
    } catch (e) {
      toast.error(e?.message || 'Failed to load purchase orders');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const kpis = useMemo(() => {
    const open = rows.filter((r) => {
      const s = String(r.status || '').toLowerCase();
      return !s.includes('received') && !s.includes('cancel') && !s.includes('draft');
    });
    const pending = rows.filter((r) =>
      String(r.status || '').toLowerCase().includes('pending'),
    );
    const value = open.reduce((sum, r) => sum + (Number(r.total) || 0), 0);
    const overdue = rows.filter(isOverdue).length;
    return { open: open.length, pending: pending.length, value, overdue };
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (tab !== 'all' && r.status !== tab) return false;
      if (!q) return true;
      return `${r.poNumber} ${r.supplier?.name} ${r.requestedBy}`
        .toLowerCase()
        .includes(q);
    });
  }, [rows, tab, search]);

  useEffect(() => setPage(1), [tab, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const pageRows = filtered.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);

  const handleDelete = async (row) => {
    if (!await appConfirmMessage(`Delete ${row.poNumber}?`)) return;
    try {
      await graphqlRequest(REMOVE, { id: row.id });
      toast.success('Purchase order deleted');
      await load();
    } catch (e) {
      toast.error(e?.message || 'Failed to delete');
    }
  };

  return (
    <div className="iproc-page">
      <header className="iproc-header">
        <h1>Purchase Orders</h1>
        <button
          type="button"
          className="iproc-btn iproc-btn--primary"
          onClick={() => overlay.openAdd()}
        >
          <FaPlus /> New Purchase Order
        </button>
      </header>

      <section className="iproc-stats">
        <article className="iproc-stat">
          <div className="iproc-stat-icon iproc-stat-icon--blue"><FaShoppingCart /></div>
          <div>
            <div className="iproc-stat-value">{kpis.open}</div>
            <div className="iproc-stat-label">Open POs</div>
          </div>
        </article>
        <article className="iproc-stat">
          <div className="iproc-stat-icon iproc-stat-icon--orange"><FaClock /></div>
          <div>
            <div className="iproc-stat-value">{kpis.pending}</div>
            <div className="iproc-stat-label">Pending Approval</div>
          </div>
        </article>
        <article className="iproc-stat">
          <div className="iproc-stat-icon iproc-stat-icon--purple"><FaDollarSign /></div>
          <div>
            <div className="iproc-stat-value">{formatAUD(kpis.value)}</div>
            <div className="iproc-stat-label">Total PO Value</div>
          </div>
        </article>
        <article className="iproc-stat">
          <div className="iproc-stat-icon iproc-stat-icon--red"><FaExclamationTriangle /></div>
          <div>
            <div className="iproc-stat-value">{kpis.overdue}</div>
            <div className="iproc-stat-label">Overdue</div>
          </div>
        </article>
      </section>

      <div className="iproc-panel">
        <div className="iproc-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`iproc-tab ${tab === t.id ? 'is-active' : ''}`}
              onClick={() => overlay.setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="iproc-toolbar">
          <label className="iproc-search">
            <FaSearch />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search purchase orders..."
            />
          </label>
        </div>

        <div className="iproc-table-wrap">
          <table className="iproc-table">
            <thead>
              <tr>
                <th>PO #</th>
                <th>Supplier</th>
                <th>Items</th>
                <th>Total Value</th>
                <th>Created</th>
                <th>Expected Delivery</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" className="iproc-empty">Loading purchase orders…</td></tr>
              ) : pageRows.length === 0 ? (
                <tr><td colSpan="8" className="iproc-empty">No purchase orders yet.</td></tr>
              ) : (
                pageRows.map((row) => (
                  <tr key={row.id}>
                    <td className="iproc-name">{row.poNumber}</td>
                    <td>{row.supplier?.name || '—'}</td>
                    <td>{row.lineCount ?? row.lines?.length ?? 0}</td>
                    <td>{formatAUD(row.total)}</td>
                    <td>{formatDate(row.orderDate)}</td>
                    <td>{formatDate(row.expectedDelivery)}</td>
                    <td>
                      <span className={`iproc-pill ${statusClass(row.status)}`}>
                        {statusLabel(row.status)}
                      </span>
                    </td>
                    <td>
                      <div className="iproc-actions-cell">
                        <button type="button" className="iproc-icon-btn" title="View" onClick={() => overlay.openView(row)}>
                          <FaEye />
                        </button>
                        <button
                          type="button"
                          className="iproc-icon-btn"
                          title="Edit"
                          onClick={() => overlay.openEdit(row)}
                        >
                          <FaEdit />
                        </button>
                        <button type="button" className="iproc-icon-btn danger" title="Delete" onClick={() => void handleDelete(row)}>
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="iproc-foot">
          <span>Showing {pageRows.length} of {filtered.length} purchase orders</span>
          <div className="iproc-pages">
            {Array.from({ length: totalPages }, (_, i) => i + 1).slice(0, 8).map((n) => (
              <button
                key={n}
                type="button"
                className={`iproc-page-btn ${pageSafe === n ? 'is-active' : ''}`}
                onClick={() => setPage(n)}
              >
                {n}
              </button>
            ))}
          </div>
        </div>
      </div>

      {overlay.showForm ? (
        <PurchaseOrderForm
          editData={overlay.editing}
          onClose={overlay.closeOverlay}
          onSaved={() => {
            overlay.closeOverlay();
            void load();
          }}
        />
      ) : null}

      {overlay.viewing ? (
        <PurchaseOrderDetailModal
          po={overlay.viewing}
          onClose={overlay.closeOverlay}
          onEdit={overlay.openEdit}
        />
      ) : null}
    </div>
  );
}
